import { useState, FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Luggage } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuthStore } from "@/stores/authStore";
import { forceDelay } from "@/lib/latency";
import { AUTH_SMOOTH_DELAY } from "@/lib/constants";
import { LoginFormProps } from "@/types/componentsTypes";

export function LoginForm({
  onToggleToRegister,
  onForgotPassword,
  onSubmit,
}: LoginFormProps) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    emailAddress: "",
    password: "",
  });
  const isUserDataLoading = useAuthStore((s) => s.isUserDataLoading);
  const authError = useAuthStore((s) => s.error);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isEmail = (v: string) => /\S+@\S+\.\S+/.test(v);
  const canSubmit =
    isEmail(formData.emailAddress) && formData.password.trim().length > 0;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || isUserDataLoading) return;
    try {
      await Promise.allSettled([
        Promise.resolve(onSubmit(formData)),
        forceDelay(AUTH_SMOOTH_DELAY),
      ]);
    } catch {}
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-full bg-brand-accent">
          <Luggage className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">
          {t("welcomeBack")}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label
            htmlFor="emailAddress"
            className="text-sm font-medium text-foreground"
          >
            {t("email")}
          </Label>
          <Input
            id="emailAddress"
            type="email"
            placeholder={t("emailPlaceholder")}
            value={formData.emailAddress}
            onChange={(e) => handleInputChange("emailAddress", e.target.value)}
            required
            className="h-11 px-4 py-3 text-[15px] placeholder:text-muted-foreground/70 rounded-xl transition-all duration-200 focus:shadow-lg focus:shadow-primary/10"
          />
          {formData.emailAddress.length > 0 &&
            !isEmail(formData.emailAddress) && (
              <p className="text-xs text-red-600">{t("invalidEmail")}</p>
            )}
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="password"
            className="text-sm font-medium text-foreground"
          >
            {t("password")}
          </Label>
          <Input
            id="password"
            type="password"
            placeholder={t("passwordPlaceholder")}
            value={formData.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            required
            className="h-11 px-4 py-3 text-[15px] placeholder:text-muted-foreground/70 rounded-xl transition-all duration-200 focus:shadow-lg focus:shadow-primary/10"
          />
        </div>

        {authError && (
          <p className="text-sm text-red-600 animate-fade-in">{authError}</p>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-primary hover:text-primary/80 transition-colors duration-200 hover:underline"
          >
            {t("forgotPassword")}
          </button>
        </div>

        <Button
          type="submit"
          disabled={!canSubmit || isUserDataLoading}
          aria-busy={isUserDataLoading}
          className="w-full h-12 bg-brand-accent disabled:opacity-60 disabled:cursor-not-allowed hover:bg-brand-accent/90 text-white font-medium text-base transition-all duration-300 hover:shadow-lg hover:shadow-brand-accent/20"
        >
          {isUserDataLoading ? t("submitLogin") : t("loginAccount")}
        </Button>

        <div className="text-center pt-2">
          <button
            type="button"
            onClick={onToggleToRegister}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            {t("noAccount")}
          </button>
        </div>
      </form>
    </div>
  );
}
